import React, { ReactElement } from 'react';
import {
  Button, Checkbox, CheckboxGroup, Col, FormGroup,
  Input, Label, Row, Textarea, LoaderButton,
} from 'idsk-react-components';
import { ConfirmModal } from 'slovlex-idsk-react-components';
import ClassType from '../../../model/pripomienka/ClassType';
import ZasadnaFormComponent from './ZasadnaFormComponent';
import SavePripomienka from '../../../model/pripomienka/SavePripomienka';
import { SafeParameterSetter } from '../../../types/SafeParameterSetter';
import { useUserContext } from '../../../context/UserContext';
import { hasRole } from '../../../model/currentuser/CurrentUserDetail';
import UserRole from '../../../model/currentuser/UserRole';
import './addPripomienkaForm.scss';

interface Props {
  classType: ClassType;
  onSubmit: () => void;
  onReset: () => void;
  getFieldErrorMessage: (fieldName: string) => string;
  onUpdateField: SafeParameterSetter<SavePripomienka>;
  saveLoading?: boolean;
  pripomienka: SavePripomienka;
}

const AddPripomienkaForm = ({
  classType,
  onSubmit,
  onReset,
  getFieldErrorMessage,
  onUpdateField,
  saveLoading,
  pripomienka,
}: Props): ReactElement => {
  const { pouzivatel } = useUserContext();
  const isPovinnyPripomienkovatel = pouzivatel && pouzivatel.isPovinnyPripomienkovatel;
  const isDalsiPripomienkovatel = pouzivatel && pouzivatel.isDalsiPripomienkovatel;

  const onCelyMaterialClick = (checked: boolean): void => {
    onUpdateField('celyMaterial', checked);

    if (checked) {
      onUpdateField('clanok', '');
      onUpdateField('paragraf', '');
      onUpdateField('odsek', '');
      onUpdateField('ine', '');
    }
  };

  const labelClassName: string = pripomienka.celyMaterial ? 'pripomienkak-label-disabled' : null;

  return (
    <form className="add-pripomienka-form">
      <FormGroup error={!!getFieldErrorMessage('pripomienkaK')}>
        <Label>Pripomienka k</Label>
        <span className="idsk-hint">Zadajte označenie ustanovenia, ktorého sa pripomienka
          týka. Vyplnenie minimálne jednej z možností je povinné.
        </span>
        <Label className="idsk-error-message">{getFieldErrorMessage('pripomienkaK')}</Label>
      </FormGroup>
      <Row>
        <Col width="one-half" className="fields-column">
          <Col width="one-third">
            <FormGroup error={!!getFieldErrorMessage('clanok')}>
              <Label className={labelClassName} htmlFor="clanok">Článku</Label>
              <Input
                id="clanok"
                rows={1}
                type="text"
                maxLength="20"
                name="clanok"
                className="pripomienkak-input"
                disabled={pripomienka.celyMaterial}
                value={pripomienka.clanok}
                autoComplete="off"
                error={getFieldErrorMessage('clanok')}
                onChange={({ target }) => onUpdateField('clanok', target.value)}
              />
            </FormGroup>
          </Col>
          <Col width="one-third">
            <FormGroup error={!!getFieldErrorMessage('paragraf')}>
              <Label className={labelClassName} htmlFor="paragraf">Paragrafu</Label>
              <Input
                id="paragraf"
                rows={1}
                type="text"
                maxLength="20"
                name="paragraf"
                className="pripomienkak-input"
                disabled={pripomienka.celyMaterial}
                value={pripomienka.paragraf}
                autoComplete="off"
                error={getFieldErrorMessage('paragraf')}
                onChange={({ target }) => onUpdateField('paragraf', target.value)}
              />
            </FormGroup>
          </Col>
          <Col width="one-third">
            <FormGroup error={!!getFieldErrorMessage('odsek')}>
              <Label className={labelClassName} htmlFor="odsek">Odseku</Label>
              <Input
                id="odsek"
                rows={1}
                type="text"
                maxLength="20"
                name="odsek"
                className="pripomienkak-input"
                autoComplete="off"
                disabled={pripomienka.celyMaterial}
                value={pripomienka.odsek}
                error={getFieldErrorMessage('odsek')}
                onChange={({ target }) => onUpdateField('odsek', target.value)}
              />
            </FormGroup>
          </Col>
        </Col>
        <Col width="one-half" className="fields-column">
          <Col width="one-third">
            <CheckboxGroup className="add-pripomienka-checkbox-wrapper">
              <Checkbox
                className="add-pripomienka-checkbox"
                id="celyMaterial"
                onClick={({ target }) => onCelyMaterialClick(target.checked)}
              >
                Celému materiálu
              </Checkbox>
            </CheckboxGroup>
          </Col>
          <Col width="two-thirds">
            <FormGroup error={!!getFieldErrorMessage('ine')}>
              <Label className={labelClassName} htmlFor="ine">Iné</Label>
              <Input
                id="ine"
                rows={1}
                maxLength="120"
                name="ine"
                disabled={pripomienka.celyMaterial}
                type="text"
                className="pripomienkak-input"
                value={pripomienka.ine}
                autoComplete="off"
                error={getFieldErrorMessage('ine')}
                onChange={({ target }) => onUpdateField('ine', target.value)}
              />
            </FormGroup>
          </Col>
        </Col>
      </Row>
      <FormGroup error={!!getFieldErrorMessage('text')}>
        <Label htmlFor="text">Text pripomienky</Label>
        <Textarea
          id="text"
          rows={15}
          maxLength={65000}
          name="text"
          error={(getFieldErrorMessage('text'))}
          onChange={({ target }) => onUpdateField('text', target.value)}
        />
      </FormGroup>
      <Row>
        <Col width="one-half" className="zasadna-container">
          {(isPovinnyPripomienkovatel || isDalsiPripomienkovatel) && (
            <ZasadnaFormComponent
              isZasadna={pripomienka.zasadna}
              classType={classType}
              updateField={onUpdateField}
              error={getFieldErrorMessage('zasadna')}
            />
          )}
        </Col>
        <Col width="one-half">
          <div className="add-pripomienka__form-buttons-container">
            {hasRole(pouzivatel, UserRole.VEREJNOST) && (
              <ConfirmModal
                question="Naozaj si želáte pridať danú pripomienku? Po pridaní ju už nebude možné
              vymazať ani upraviť a jej obsah sa zobrazí na portáli."
                cancelButtonText="Nie"
                confirmButton={(<Button buttonStyle="action" size="large">Áno</Button>)}
                openButton={(
                  <LoaderButton
                    loading={saveLoading}
                    buttonStyle="action"
                    type="button"
                    size="large"
                  >
                    Pridať
                  </LoaderButton>
            )}
                doAfterConfirm={onSubmit}
              />
            )}
            {!hasRole(pouzivatel, UserRole.VEREJNOST) && (
              <LoaderButton
                loading={saveLoading}
                type="button"
                onClick={onSubmit}
                size="large"
              >Pridať
              </LoaderButton>
            )}
            <Button onClick={onReset} type="reset" buttonStyle="secondary" size="large">Zrušiť</Button>
          </div>
        </Col>
      </Row>
    </form>
  );
};

export default AddPripomienkaForm;
